import { toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import { createInjectedState } from './injectedState'

const { provideState, useState } = createInjectedState<boolean>(
  'shared-loading-state',
  false
)

/**
 * Announce to everything rendered inside that the record filling this block has not arrived
 * yet. `Modal` provides its own `loading` prop, so a form inside one already has this —
 * call it by hand only for a block that loads a record outside a modal.
 *
 * `undefined` is coerced here rather than accepted as a third state: a block that has not
 * decided yet is not loading, and the consumers below branch on a boolean.
 */
export function provideLoadingState(
  source: MaybeRefOrGetter<boolean | undefined>
) {
  provideState(() => !!toValue(source))
}

/**
 * Whether the nearest enclosing block is still loading its record — `false` when nothing
 * announced it, so a component used outside such a block behaves as if nothing
 * were loading.
 *
 * What it is for: a control that seeds itself from the record can hold its request until
 * the record lands, instead of firing once without it and again the moment it appears. A
 * nested block shadows the outer one, which is what its own children should see.
 */
export const useLoadingState: () => ComputedRef<boolean> = useState
